import React, { useState, useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { SunIcon, MoonIcon, MenuIcon, XIcon, HomeIcon, UserIcon, LayersIcon, PricingIcon, AIAgentIcon, CodeIcon, DashboardIcon, LoginIcon } from './Icons';
import { Page } from '../types';

interface HeaderProps {
  theme: string;
  toggleTheme: () => void;
  navigateTo: (page: Page, anchor?: string) => void;
}

const NavButton: React.FC<{ onClick: () => void; children: React.ReactNode; className?: string; disabled?: boolean; }> = ({ onClick, children, className, disabled }) => (
    <button onClick={onClick} disabled={disabled} className={`px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
        {children}
    </button>
);

const MobileNavButton: React.FC<{ onClick: () => void; children: React.ReactNode; icon: React.ReactNode; disabled?: boolean; }> = ({ onClick, children, icon, disabled }) => (
    <button onClick={onClick} disabled={disabled} className={`w-full flex items-center space-x-4 p-4 text-lg text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}>
        {icon}
        <span>{children}</span>
    </button>
);


const Header: React.FC<HeaderProps> = ({ theme, toggleTheme, navigateTo }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, login, signup, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleNavClick = (page: Page, anchor?: string) => {
    setIsMenuOpen(false);
    navigateTo(page, anchor);
  };

  const navLinks = [
      { label: "Home", page: 'home' as Page, icon: <HomeIcon /> },
      { label: "About", page: 'about' as Page, icon: <UserIcon /> },
      { label: "Templates", page: 'templates' as Page, icon: <LayersIcon /> },
      { label: "Mobile Deploy", page: 'mobile_deployment' as Page, icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg> },
      { label: "Pricing", page: 'pricing' as Page, icon: <PricingIcon /> },
      { label: "AI Agents", page: 'ai_agents' as Page, icon: <AIAgentIcon /> },
  ];

  if (isAuthenticated) {
    navLinks.splice(1, 0, { label: "Dashboard", page: 'dashboard' as Page, icon: <DashboardIcon /> });
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[var(--header-bg)] backdrop-blur-lg border-b border-[var(--border-color)]' : 'bg-transparent'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          <button onClick={() => handleNavClick('home')} className="flex items-center space-x-2">
             <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7V17L12 22L22 17V7L12 2ZM12 12.33L5.5 8.67L12 5L18.5 8.67L12 12.33ZM12 14.5L18.5 10.83V16.33L12 20V14.5ZM5.5 10.83L12 14.5V20L5.5 16.33V10.83Z" fill="url(#logoGradient)"/>
                <defs>
                  <linearGradient id="logoGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="var(--gradient-from)" />
                    <stop offset="100%" stopColor="var(--gradient-to)" />
                  </linearGradient>
                </defs>
              </svg>
            <span className="text-xl font-bold text-[var(--text-primary)]">Codemine</span>
          </button>
          
          <nav className="hidden lg:flex items-center space-x-2">
            {navLinks.map(link => (
                <NavButton key={link.page} onClick={() => handleNavClick(link.page)}>{link.label}</NavButton>
            ))}
          </nav>

          <div className="flex items-center space-x-4">
            <button onClick={toggleTheme} aria-label="Toggle theme" className="p-2 rounded-full text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
             <div className="hidden lg:flex items-center space-x-2">
                {isAuthenticated ? (
                  <>
                    <button
                        onClick={() => navigateTo('workspace')}
                        className="flex items-center space-x-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-md hover:opacity-90 transition-opacity"
                    >
                        <CodeIcon />
                        <span>Workspace</span>
                    </button>
                    <NavButton onClick={() => logout()}>Log Out</NavButton>
                  </>
                ) : (
                  <>
                    <NavButton onClick={() => login()}>Log In</NavButton>
                  </>
                )}
            </div>
            
            <div className="lg:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                {isMenuOpen ? <XIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="lg:hidden bg-slate-900/90 backdrop-blur-xl absolute top-full left-0 right-0 p-4 border-t border-[var(--border-color)] h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="flex flex-col space-y-2">
            {navLinks.map(link => (
                <MobileNavButton key={link.page} onClick={() => handleNavClick(link.page)} icon={link.icon}>{link.label}</MobileNavButton>
             ))}
             <div className="pt-4 mt-4 border-t border-white/10">
                {isAuthenticated ? (
                    <>
                        <MobileNavButton onClick={() => handleNavClick('workspace')} icon={<CodeIcon />}>Workspace</MobileNavButton>
                        <MobileNavButton onClick={() => logout()} icon={<LoginIcon />}>Log Out</MobileNavButton>
                    </>
                ) : (
                    <>
                        <MobileNavButton onClick={() => login()} icon={<LoginIcon />}>Log In</MobileNavButton>
                    </>
                )}
             </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;